import React from 'react';
import CircleGreen from '@lib/illustrations/circle-green.svg';
import CircleCyan from '@lib/illustrations/circle-cyan.svg';
import CircleOrange from '@lib/illustrations/circle-orange.svg';
import CircleRed from '@lib/illustrations/circle-red.svg';
import { i18n } from '@lib/i18n';
import { t } from '@lingui/macro';
import { LoadingCircle, CheckBadge, SearchScan, ClockDash } from '@oca/icons';

export const statuses = [
  { label: i18n._(t`To do`), value: 'todo', icon: <LoadingCircle /> },
  {
    label: i18n._(t`In progress`),
    value: 'in_progress',
    icon: <ClockDash color="#F79009" />,
  },
  { label: i18n._(t`In review`), value: 'in_review', icon: <SearchScan /> },
  {
    label: i18n._(t`Done`),
    value: 'done',
    icon: <CheckBadge color="#17B26A" />,
  },
];

export const priorities = [
  {
    value: 'low',
    avatar: CircleGreen,
    color: '#2BBF11',
    label: i18n._(t`Low`),
  },
  {
    value: 'medium',
    avatar: CircleCyan,
    color: '#009EFF',
    label: i18n._(t`Medium`),
  },
  {
    value: 'high',
    avatar: CircleOrange,
    color: '#FEA201',
    label: i18n._(t`High`),
  },
  {
    value: 'highest',
    avatar: CircleRed,
    color: '#FE2400',
    label: i18n._(t`Highest`),
  },
];

// --- Recurrence -----------------------------------------------------------
// Option `value`s intentionally match the API literally (snake_case), because
// the request transform only snake_cases object *keys*, not string values.

export const recurrenceFrequencies = [
  { value: 'daily', label: i18n._(t`Daily`) },
  { value: 'weekly', label: i18n._(t`Weekly`) },
  { value: 'monthly', label: i18n._(t`Monthly`) },
];

// Backend weekday indexing: 0 = Monday … 6 = Sunday
export const weekDays = [
  { value: 0, short: i18n._(t`Mon`), label: i18n._(t`Monday`) },
  { value: 1, short: i18n._(t`Tue`), label: i18n._(t`Tuesday`) },
  { value: 2, short: i18n._(t`Wed`), label: i18n._(t`Wednesday`) },
  { value: 3, short: i18n._(t`Thu`), label: i18n._(t`Thursday`) },
  { value: 4, short: i18n._(t`Fri`), label: i18n._(t`Friday`) },
  { value: 5, short: i18n._(t`Sat`), label: i18n._(t`Saturday`) },
  { value: 6, short: i18n._(t`Sun`), label: i18n._(t`Sunday`) },
];

export const monthlyModes = [
  { value: 'day_of_month', label: i18n._(t`On day of month`) },
  { value: 'weekday_pattern', label: i18n._(t`On weekday pattern`) },
];

// `value` is the ordinal week of the month; -1 means the last occurrence.
export const monthlyWeeks = [
  { value: 1, label: i18n._(t`first`) },
  { value: 2, label: i18n._(t`second`) },
  { value: 3, label: i18n._(t`third`) },
  { value: 4, label: i18n._(t`fourth`) },
  { value: -1, label: i18n._(t`last`) },
];

export const endTypes = [
  { value: 'never', label: i18n._(t`Never`) },
  { value: 'on_date', label: i18n._(t`On date`) },
  { value: 'after_occurrences', label: i18n._(t`After occurrences`) },
];

// Edit-scope for recurring tasks (sent as `recurrence_scope`).
export const recurrenceScopes = [
  { value: 'occurrence', label: i18n._(t`This occurrence`) },
  { value: 'series', label: i18n._(t`Entire series`) },
];

/**
 * Builds default recurrence form values, seeding the monthly options from the
 * task's due date so the preview is sensible the moment recurrence is enabled.
 * @param {Date|string|number} [dueDate]
 */
export function defaultRecurrence(dueDate) {
  const date = dueDate ? new Date(dueDate) : new Date();
  const dayOfMonth = date.getDate();
  // JS getDay(): 0 = Sunday … 6 = Saturday → convert to Mon=0 … Sun=6
  const weekday = (date.getDay() + 6) % 7;
  // 1-based ordinal of this weekday within the month (1st, 2nd, … Monday)
  const weekOfMonth = Math.floor((dayOfMonth - 1) / 7) + 1;

  return {
    frequency: 'daily',
    interval: 1,
    weeklyDays: [weekday],
    monthlyMode: 'day_of_month',
    monthlyDay: dayOfMonth,
    monthlyWeek: weekOfMonth,
    monthlyWeekday: weekday,
    endType: 'never',
    endDate: null,
    occurrences: 1,
  };
}
